const COPY = {
  cs: {
    title: (metric) => `Hloubkový test ${metric} se nepodařilo dokončit`,
    causes: {
      timeout: (metric) => metric === 'LCP'
        ? 'Měření překročilo časový limit při načítání nebo čekání na stabilní LCP.'
        : `Měření ${metric} překročilo časový limit dříve, než vznikl platný výsledek.`,
      crash: 'Měřicí prohlížeč během běhu spadl nebo uzavřel stránku.',
      memory: 'Běh skončil prokázaným nedostatkem paměti měřicího prostředí.',
      'capture-timing': (metric) => metric === 'LCP'
        ? 'Stránka se načetla, ale měřicí vrstva nedokázala spolehlivě zachytit časování LCP.'
        : metric === 'INP'
          ? 'Stránka se načetla, ale měřicí vrstva nezachytila platnou interakci s důvěryhodným časováním a atribucí.'
          : `Stránka se načetla, ale měřicí vrstva nedokázala spolehlivě zachytit časování ${metric}.`,
      unreproducible: (metric) => metric === 'LCP'
        ? 'V dostupném měřicím okně se nepodařilo zachytit skutečný a ustálený LCP.'
        : `V dostupném měřicím okně se nepodařilo získat platné měření ${metric}.`,
      'budget-exhausted': (metric) => metric === 'LCP'
        ? 'Audit vyčerpal bezpečný časový rozpočet dříve, než mohl LCP dokončit.'
        : `Audit vyčerpal bezpečný časový rozpočet dříve, než mohl dokončit měření ${metric}.`,
      unknown: 'Hloubkový běh skončil neočekávanou technickou chybou.'
    },
    heavyPage: 'Podpůrné signály ukazují na těžkou stránku s velkým množstvím zdrojů; to je pravděpodobná příčina nestability měření.',
    available: {
      sweep: (metric) => `Field p75 zůstává autoritativní. K dispozici je také orientační multi-page sweep, který není náhradou za hloubkovou analýzu ${metric}.`,
      diagnostics: (metric) => `Field p75 zůstává autoritativní. K dispozici zůstala základní PSI diagnostika, nikoli hloubkový rozpad ${metric}.`,
      field: (metric) => `K dispozici zůstává field p75 z reálných uživatelů; laboratorní hodnota ${metric} ani její rozpad nejsou dostupné.`
    },
    actionRetry: 'Nejprve zopakujte měření na stejné URL a zjistěte, ve které fázi selhává; opakování může dodat platná data, ale samo o sobě nezaručuje určení příčiny.',
    actionLighter: 'Pokud se stav opakuje, lze navíc otestovat jinou reprezentativní URL stejného typu. Jde však o odlišný diagnostický scénář, který nenahrazuje vyšetření původní URL a může vynechat zátěž související s jejím problémem.',
    labels: {
      cause: 'Pravděpodobná příčina',
      available: 'Co je stále k dispozici',
      next: 'Další krok'
    }
  },
  en: {
    title: (metric) => `${metric} deep test could not be completed`,
    causes: {
      timeout: (metric) => metric === 'LCP'
        ? 'The measurement exceeded its time limit while loading or waiting for a stable LCP.'
        : `The ${metric} measurement exceeded its time limit before producing a valid result.`,
      crash: 'The measurement browser crashed or closed the page during the run.',
      memory: 'The run ended with confirmed memory pressure in the measurement environment.',
      'capture-timing': (metric) => metric === 'LCP'
        ? 'The page loaded, but the measurement layer could not capture trustworthy LCP timing.'
        : metric === 'INP'
          ? 'The page loaded, but the measurement layer captured no valid interaction with trustworthy timing and attribution.'
          : `The page loaded, but the measurement layer could not capture trustworthy ${metric} timing.`,
      unreproducible: (metric) => metric === 'LCP'
        ? 'No real, stable LCP could be observed within the available measurement window.'
        : `No valid ${metric} measurement could be obtained within the available measurement window.`,
      'budget-exhausted': (metric) => metric === 'LCP'
        ? 'The audit exhausted its safe time budget before LCP could complete.'
        : `The audit exhausted its safe time budget before the ${metric} measurement could complete.`,
      unknown: 'The deep run ended with an unexpected technical error.'
    },
    heavyPage: 'Supporting signals point to a heavy page with many resources; this is the likely source of the measurement instability.',
    available: {
      sweep: (metric) => `Field p75 remains authoritative. An indicative multi-page sweep is also available, but it is not a replacement for deep ${metric} analysis.`,
      diagnostics: (metric) => `Field p75 remains authoritative. Basic PSI diagnostics are still available, but not the deep ${metric} breakdown.`,
      field: (metric) => `Field p75 from real users remains available; no lab ${metric} value or breakdown is available.`
    },
    actionRetry: 'First repeat the measurement on the same URL and identify the stage where it fails; a repeat may provide valid data, but does not by itself guarantee that a cause will be identified.',
    actionLighter: 'If the state repeats, another representative URL of the same page type may also be tested. That is a different diagnostic scenario, however: it does not replace investigating the original URL and may omit load related to its problem.',
    labels: {
      cause: 'Likely cause',
      available: 'Data still available',
      next: 'Next step'
    }
  }
};

export function getDeepFailurePresentation(
  failure,
  language = 'cs',
  { metric = 'LCP', hasSweep = false, hasDiagnostics = false } = {}
) {
  const lang = language === 'en' ? 'en' : 'cs';
  const copy = COPY[lang];
  const type = failure?.type || 'unknown';
  const metricUpper = String(metric || 'LCP').toUpperCase();
  const causeCopy = copy.causes[type] || copy.causes.unknown;
  const cause = failure?.probableCause === 'heavy-page'
    ? copy.heavyPage
    : (typeof causeCopy === 'function' ? causeCopy(metricUpper) : causeCopy);
  const actions = Array.isArray(failure?.recommendedActions)
    ? failure.recommendedActions
    : ['retry'];
  const action = [
    copy.actionRetry,
    ...(actions.includes('lighter-url') ? [copy.actionLighter] : [])
  ].join(' ');
  const availableCopy = hasSweep
    ? copy.available.sweep
    : hasDiagnostics
      ? copy.available.diagnostics
      : copy.available.field;
  const available = typeof availableCopy === 'function' ? availableCopy(metricUpper) : availableCopy;

  return {
    type,
    title: copy.title(metricUpper),
    cause,
    available,
    action,
    labels: copy.labels
  };
}